import { useRef } from "react";
import { FileText, Paperclip, Trash2, Upload, Check, Plus } from "lucide-react";
import { MOU_DOCUMENT_ACCEPT, MOU_DOCUMENT_HINT, formatSizeBytes, formatInstant } from "./mouCaseShared.js";

/**
 * P164: the documents belonging to one step, presented as the step's own
 * checklist rather than as a bare file list.
 *
 * Each required document is a NAMED slot. Picking a file against a slot uploads
 * it already labelled with that slot's exact name, which is what lets the server
 * tick the checklist row off - a free-text label the operator retyped would
 * almost never match, and the count would then never go down. The free-text box
 * underneath is still there, because the paperwork a province or an employer
 * asks for on top of the standard set varies and must not be un-attachable.
 */

/** One checklist row: what it is, whether it has arrived, and how to attach it. */
function DocumentSlot({ label, documents, disabled, onPick, onRemove, onOpen }) {
  const inputRef = useRef(null);
  const attached = documents.filter((document) => document.documentType === label);
  const satisfied = attached.length > 0;

  return (
    <li className={`wx-mou-slot ${satisfied ? "wx-mou-slot--done" : ""}`}>
      <span className="wx-mou-slot-mark" aria-hidden="true">
        {satisfied ? <Check size={13} /> : <FileText size={13} />}
      </span>

      <div className="wx-mou-slot-main">
        <div className="wx-mou-slot-head">
          <span className="wx-mou-slot-label">{label}</span>
          <span className={`wx-badge wx-badge--${satisfied ? "success" : "neutral"}`}>
            {satisfied ? "แนบแล้ว" : "ยังไม่แนบ"}
          </span>
        </div>

        {attached.length > 0 && (
          <ul className="wx-mou-file-list">
            {attached.map((document) => (
              <AttachedFile key={document.documentId} document={document} disabled={disabled} onRemove={onRemove} onOpen={onOpen} />
            ))}
          </ul>
        )}
      </div>

      <button
        type="button"
        className="wx-mou-slot-pick"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <Upload size={13} aria-hidden="true" />
        {satisfied ? "แนบเพิ่ม" : "แนบไฟล์"}
      </button>

      <input
        ref={inputRef}
        type="file"
        className="wx-visually-hidden"
        aria-label={`แนบไฟล์ ${label}`}
        accept={MOU_DOCUMENT_ACCEPT}
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so picking the same file twice still fires a change.
          if (inputRef.current) {
            inputRef.current.value = "";
          }
          if (file) {
            onPick(file, label);
          }
        }}
      />
    </li>
  );
}

/** One stored file: what it is, and the two things you can do with it. */
function AttachedFile({ document, disabled, onRemove, onOpen }) {
  const size = formatSizeBytes(document.sizeBytes);
  const uploadedAt = formatInstant(document.uploadedAtUtc);
  const name = document.fileName || document.documentType || "เอกสารแนบ";

  return (
    <li className="wx-mou-file">
      <span className="wx-mou-file-icon" aria-hidden="true">
        <Paperclip size={13} />
      </span>
      <button type="button" className="wx-mou-file-open" onClick={() => onOpen(document)}>
        {name}
      </button>
      <span className="wx-mou-file-meta">{[size, uploadedAt].filter(Boolean).join(" · ")}</span>
      <button
        type="button"
        className="wx-mou-file-remove"
        aria-label={`ลบเอกสาร ${name}`}
        disabled={disabled}
        onClick={() => onRemove(document)}
      >
        <Trash2 size={13} aria-hidden="true" />
      </button>
    </li>
  );
}

export default function MouStepDocuments({
  requiredDocuments,
  documents,
  disabled,
  extraLabel,
  onExtraLabelChange,
  onPick,
  onRemove,
  onOpen,
}) {
  const extraInputRef = useRef(null);
  const requiredSet = new Set(requiredDocuments);
  const extras = documents.filter((document) => !document.documentType || !requiredSet.has(document.documentType));

  return (
    <div className="wx-mou-docs">
      <div className="wx-mou-docs-head">
        <h4>เอกสารของขั้นตอนนี้</h4>
        <p>{MOU_DOCUMENT_HINT} — ไฟล์จะถูกบันทึกทันทีที่เลือก ไม่ต้องกดบันทึกซ้ำ</p>
      </div>

      <ul className="wx-mou-slots">
        {requiredDocuments.map((label) => (
          <DocumentSlot
            key={label}
            label={label}
            documents={documents}
            disabled={disabled}
            onPick={onPick}
            onRemove={onRemove}
            onOpen={onOpen}
          />
        ))}
      </ul>

      <div className="wx-mou-extra">
        <label className="wx-mou-extra-label" htmlFor="wx-mou-extra-type">
          เอกสารอื่นของขั้นตอนนี้
        </label>
        <p className="wx-mou-hint">
          เอกสารที่ไม่อยู่ในรายการข้างบน เช่น ใบนัดหมาย สำเนาอีเมล หรือเอกสารที่สำนักงานจัดหางานขอเพิ่ม — พิมพ์ชื่อเอกสารก่อนเลือกไฟล์
        </p>
        <div className="wx-mou-extra-row">
          <input
            id="wx-mou-extra-type"
            type="text"
            placeholder="ชื่อเอกสาร เช่น ใบนัดหมายเจ้าหน้าที่"
            maxLength={120}
            value={extraLabel}
            disabled={disabled}
            onChange={(event) => onExtraLabelChange(event.target.value)}
          />
          <button
            type="button"
            className="wx-button wx-button--ghost"
            disabled={disabled}
            onClick={() => extraInputRef.current?.click()}
          >
            <Plus size={14} aria-hidden="true" />
            เลือกไฟล์
          </button>
          <input
            ref={extraInputRef}
            type="file"
            className="wx-visually-hidden"
            aria-label="แนบเอกสารอื่นของขั้นตอนนี้"
            accept={MOU_DOCUMENT_ACCEPT}
            disabled={disabled}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (extraInputRef.current) {
                extraInputRef.current.value = "";
              }
              if (file) {
                onPick(file, extraLabel.trim() || null);
              }
            }}
          />
        </div>

        {extras.length > 0 && (
          <ul className="wx-mou-file-list wx-mou-file-list--extra">
            {extras.map((document) => (
              <AttachedFile key={document.documentId} document={document} disabled={disabled} onRemove={onRemove} onOpen={onOpen} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
